import dayjs from 'dayjs'
import { arrayOf, object, string } from 'prop-types'
import { FormattedMessage } from 'react-intl'
import Typography from '../../../../atoms/typography'
import { WorkingRowWrapper } from './working-times-table.styled'

const formatDate = (date, format) => {
	return dayjs(date.replace(' ', 'T')).format(format)
}

/**
 * @description Convert a time in *minutes-since-midnight*.
 * @param {Date} time - Time to convert.
 * @returns {Number} *minutes-since-midnight* value.
 */
const convertTime = (time) => time.getHours() * 60 + time.getMinutes()

/**
 * @description Given a working times table, return if today the is open/closed.
 * @param {Object[]} workingTimes - Working times of the store.
 * @returns {String} current open until/opens at/closed state.
 */
const OpenNow = ({ workingTimes }) => {
	const nowDay = new Date().getDay()
	const currentWorkDayIdx = workingTimes.findIndex((wt) => wt.day === nowDay)
	// current work day is morning + afternoon
	const currentWorkDay = [workingTimes[currentWorkDayIdx], workingTimes[currentWorkDayIdx + 1]]

	// Morning Time From - To
	const mtf = new Date(currentWorkDay[0].from.replace(' ', 'T'))
	const mtt = new Date(currentWorkDay[0].to.replace(' ', 'T'))
	// Afternoon Time From - To
	const atf = new Date(currentWorkDay[1].from.replace(' ', 'T'))
	const att = new Date(currentWorkDay[1].to.replace(' ', 'T'))

	const currentMorningTimeFrom = !currentWorkDay[0].closed ? convertTime(mtf) : false
	const currentMorningTimeTo = !currentWorkDay[0].closed ? convertTime(mtt) : false
	const currentAfternoonTimeFrom = !currentWorkDay[1].closed ? convertTime(atf) : false
	const currentAfternoonTimeTo = !currentWorkDay[1].closed ? convertTime(att) : false

	const nowTime = convertTime(new Date())
	const closedToday = !currentMorningTimeFrom && !currentAfternoonTimeFrom
	let isOpenNow = false
	let openSoon = false

	if (!closedToday) {
		let isOpenNowMorning = nowTime >= currentMorningTimeFrom && nowTime < currentMorningTimeTo
		let isOpenNowAfternoon = nowTime >= currentAfternoonTimeFrom && nowTime < currentAfternoonTimeTo

		openSoon =
			nowTime < currentMorningTimeFrom ? (
				<FormattedMessage
					id="store_locator_detail_opens_at"
					values={{ time: formatDate(currentWorkDay[0].from, 'HH:mm') }}
				/>
			) : nowTime < currentAfternoonTimeFrom ? (
				<FormattedMessage
					id="store_locator_detail_opens_at"
					values={{ time: formatDate(currentWorkDay[1].from, 'HH:mm') }}
				/>
			) : (
				<FormattedMessage id="store_locator_detail_closed" />
			)

		isOpenNow =
			isOpenNowMorning || isOpenNowAfternoon ? (
				<FormattedMessage
					id="store_locator_detail_open_until"
					values={{
						time: isOpenNowMorning
							? formatDate(currentWorkDay[0].to, 'HH:mm')
							: formatDate(currentWorkDay[1].to, 'HH:mm'),
					}}
				/>
			) : (
				false
			)
	}

	return closedToday ? <FormattedMessage id="store_locator_detail_closed_today" /> : isOpenNow || openSoon
}

/**
 * Use it as a function, to check if it returns a valid working time.
 * Otherwise, use it as a component if you need to style it with `styled`.
 * @example
 * WorkingTimes({workingTimes}) && <WorkingTimes workingTimes={workingTimes} />
 * TODO: 🧑🏻‍💻 refactor
 */
const WorkingTimes = ({ className, workingTimes, groupWorkingTimes }) => {
	const workingDay = []

	const weekDays = [
		<FormattedMessage key="0" id="monday" />,
		<FormattedMessage key="1" id="tuesday" />,
		<FormattedMessage key="2" id="wednesday" />,
		<FormattedMessage key="3" id="thursday" />,
		<FormattedMessage key="4" id="friday" />,
		<FormattedMessage key="5" id="saturday" />,
		<FormattedMessage key="6" id="sunday" />,
	]

	for (let i = 0; i < parseInt(workingTimes.length); i += 2) {
		let dayName

		switch (workingTimes[i].day) {
			case 1:
				dayName = <FormattedMessage id="monday" />
				break
			case 2:
				dayName = <FormattedMessage id="tuesday" />
				break
			case 3:
				dayName = <FormattedMessage id="wednesday" />
				break
			case 4:
				dayName = <FormattedMessage id="thursday" />
				break
			case 5:
				dayName = <FormattedMessage id="friday" />
				break
			case 6:
				dayName = <FormattedMessage id="saturday" />
				break
			case 7:
				dayName = <FormattedMessage id="sunday" />
				break
			default:
				dayName = ''
		}

		let morningStr = <FormattedMessage id="store_locator_detail_closed" />
		let afternoonStr = <FormattedMessage id="store_locator_detail_closed" />
		let continuedTimetable = false
		let noTimeSet = true

		const fromHours = formatDate(workingTimes[i].from, 'HH:mm')
		const toHours = formatDate(workingTimes[i].to, 'HH:mm')

		const nextFromHours = formatDate(workingTimes[i + 1].from, 'HH:mm')
		const nextToHours = formatDate(workingTimes[i + 1].to, 'HH:mm')

		if (!workingTimes[i].closed && !(fromHours == '00:00' && toHours == '00:00')) {
			noTimeSet = false
			morningStr = fromHours + ' – ' + toHours

			if (workingTimes[i].to === workingTimes[i + 1].from) {
				continuedTimetable = true
				morningStr = fromHours + ' – ' + nextToHours
			}
		}

		if (!continuedTimetable && !workingTimes[i + 1].closed && !(nextFromHours == '00:00' && nextToHours == '00:00')) {
			noTimeSet = false
			afternoonStr = nextFromHours + ' – ' + nextToHours
		}

		if (workingTimes[i].closed && workingTimes[i + 1].closed) {
			continuedTimetable = true
		}

		if (groupWorkingTimes) {
			workingDay.push(
				<>
					<Typography component="span" color="grey.main" className="day">
						{dayName}
					</Typography>
					{!noTimeSet ? (
						<Typography component="span" color="grey.main">
							{morningStr}
							{!continuedTimetable && (
								<>
									{' / '} {afternoonStr}
								</>
							)}
						</Typography>
					) : (
						<Typography component="span" color="grey.main">
							<FormattedMessage id="store_locator_detail_closed" />
						</Typography>
					)}
				</>
			)
		} else {
			!noTimeSet
				? workingDay.push(
						<>
							<Typography component="span" color="grey.main" className="day">
								{dayName}
								{': '}
							</Typography>
							<Typography component="span" color="grey.main">
								{morningStr}
								{!continuedTimetable && (
									<>
										{' / '} {afternoonStr}
									</>
								)}
							</Typography>
						</>
				  )
				: null
		}
	}

	let countSkipped = 0

	function isArrayContentSame(a, b) {
		if (Array.isArray(a) && Array.isArray(b) && a.length == b.length) {
			a = a.concat().sort()
			b = b.concat().sort()
			return a.reduce((acc, e, i) => acc && e === b[i], true)
		} else {
			return false
		}
	}

	if (groupWorkingTimes) {
		const workingTimeTable = workingDay.length
			? Object.entries(workingDay).map(([key, day]) => {
					let skip = false
					let currentDay = weekDays[key]
					let nextKey = parseFloat(key) + 1
					let currentTimes = day?.props.children[1]?.props.children
					let nextTimes = workingDay[nextKey]?.props.children[1]?.props.children

					if (isArrayContentSame(currentTimes, nextTimes)) {
						skip = true
						countSkipped = countSkipped + 1
					} else {
						skip = false
					}

					if (!skip) {
						return (
							<WorkingRowWrapper key={key} className={className}>
								{countSkipped > 0 ? (
									<>
										{(() => {
											let daysToPrint = (
												<Typography component="span" color="grey.main" className="day">
													{weekDays[key - countSkipped]} - {currentDay}
												</Typography>
											)
											countSkipped = 0
											return daysToPrint
										})()}
									</>
								) : (
									<Typography component="span" color="grey.main" className="day">
										{currentDay}
									</Typography>
								)}
								{day.props.children[1]}
							</WorkingRowWrapper>
						)
					}
			  })
			: null
		return workingTimeTable
	} else {
		const workingTimeTable = workingDay.length
			? Object.entries(workingDay).map(([key, day]) => (
					<WorkingRowWrapper key={key} className={className}>
						{day}
					</WorkingRowWrapper>
			  ))
			: null
		return workingTimeTable
	}
}

WorkingTimes.defaultProps = { workingTimes: [] }

WorkingTimes.propTypes = {
	/**
	 * Defines className for root node.
	 */
	className: string,
	/**
	 * Defines working times.
	 */
	workingTimes: arrayOf(object),
}

export default WorkingTimes
export { OpenNow }
